/**
 * Client-side activity log stored in localStorage.
 * Tracks job views (and potentially other interactions) so the candidate dashboard
 * heatmap and streak reflect engagement beyond just submitted applications.
 *
 * No PII beyond job IDs. Capped at MAX_DAYS rolling window. Deduped per (day, jobId, type).
 */

const KEY = 'portal-activity-log';
const VERSION = 1;
const MAX_DAYS = 365;

type ActivityType = 'view';

interface LogEntry {
  d: string;            // YYYY-MM-DD (local day)
  t: ActivityType;
  j?: number;           // optional job id
}

interface LogShape {
  v: number;
  entries: LogEntry[];
}

const dayKey = (date: Date): string => {
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, '0');
  const d = `${date.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${d}`;
};

function read(): LogShape {
  if (typeof window === 'undefined') return { v: VERSION, entries: [] };
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return { v: VERSION, entries: [] };
    const parsed = JSON.parse(raw) as LogShape;
    if (!parsed || parsed.v !== VERSION || !Array.isArray(parsed.entries)) {
      return { v: VERSION, entries: [] };
    }
    return parsed;
  } catch {
    return { v: VERSION, entries: [] };
  }
}

function write(shape: LogShape): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(shape));
  } catch {
    /* swallow quota / private-mode errors */
  }
}

function trim(shape: LogShape): LogShape {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - MAX_DAYS);
  const cutoffKey = dayKey(cutoff);
  const trimmed = shape.entries.filter((e) => e.d >= cutoffKey);
  return { v: shape.v, entries: trimmed };
}

export function logJobView(jobId: number): void {
  if (!Number.isFinite(jobId)) return;
  const today = dayKey(new Date());
  const shape = trim(read());
  // Dedupe: at most one 'view' entry per (day, jobId).
  if (shape.entries.some((e) => e.d === today && e.t === 'view' && e.j === jobId)) {
    return;
  }
  shape.entries.push({ d: today, t: 'view', j: jobId });
  write(shape);
}

/** Returns ISO-date strings (YYYY-MM-DD) for all logged client-side activity in the last MAX_DAYS days. */
export function getActivityDates(): string[] {
  return trim(read()).entries.map((e) => e.d);
}

/** Returns count of unique jobs viewed in the last `days` days. */
export function getViewedJobsCount(days = 30): number {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  const cutoffKey = dayKey(cutoff);
  const log = trim(read());
  const unique = new Set<number>();
  for (const e of log.entries) {
    if (e.t === 'view' && e.d >= cutoffKey && typeof e.j === 'number') {
      unique.add(e.j);
    }
  }
  return unique.size;
}
